import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '../api';
import {
  FEATURE_TOKENS,
  FeatureToken,
  FEATURE_METADATA,
  DEFAULT_FALLBACK_FEATURES,
  QUOTA_KEYS,
  QuotaKey,
  FeatureMetadata,
  getFeatureLabel,
  getFeatureDescription
} from '../constants/features';

export {
  FEATURE_TOKENS,
  FEATURE_METADATA,
  DEFAULT_FALLBACK_FEATURES,
  QUOTA_KEYS,
  getFeatureLabel,
  getFeatureDescription
};
export type { FeatureToken, QuotaKey, FeatureMetadata };

export interface FeatureState {
  features: string[];
  quotas: Record<string, any>;
  isLoaded: boolean;
  lastSyncedAt: number | null;
  companyId: string | null;
  isSyncing: boolean;
  syncStatus: 'healthy' | 'degraded' | 'unverified' | 'licensed' | 'unknown';
  syncSource: string | null;
}

export interface FeatureSyncStatus {
  status?: FeatureState['syncStatus'];
  source?: string;
  synced_at?: string | null;
  fresh_until?: string | null;
}

export interface FeatureActions {
  setFeatures: (
    features: string[],
    quotas?: Record<string, any>,
    companyId?: string | null,
    syncStatus?: FeatureSyncStatus
  ) => void;
  hasFeature: (token: string) => boolean;
  getQuota: (key: string) => any;
  markSyncUnavailable: () => void;
  resetFeatures: () => void;
  syncFeatures: (targetCompanyId?: string | null, force?: boolean) => Promise<string[]>;
}

export type FeatureStore = FeatureState & FeatureActions;

const STORAGE_KEY = 'sawport_feature_entitlements';

const initialState: FeatureState = {
  features: [],
  quotas: {},
  isLoaded: false,
  lastSyncedAt: null,
  companyId: null,
  isSyncing: false,
  syncStatus: 'unknown',
  syncSource: null
};

// In-flight sync promise deduplication, scoped to each company.
const inFlightSync = new Map<string, Promise<string[]>>();

export const useFeatureStore = create<FeatureStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setFeatures: (
        features: string[] = [],
        quotas: Record<string, any> = {},
        companyId: string | null = null,
        syncStatus?: FeatureSyncStatus
      ) => {
        const validFeatures = Array.isArray(features) ? features : [];
        set((state) => ({
          features: validFeatures,
          quotas: quotas || {},
          isLoaded: true,
          lastSyncedAt: Date.now(),
          companyId: companyId ?? state.companyId,
          syncStatus: syncStatus?.status || 'unknown',
          syncSource: syncStatus?.source || null
        }));
      },

      hasFeature: (token: string) => {
        const { features } = get();
        if (!token) return true;
        return features.includes(token);
      },

      getQuota: (key: string) => {
        const { quotas } = get();
        return quotas?.[key] ?? null;
      },

      markSyncUnavailable: () => {
        set((state) => {
          if (state.isLoaded) {
            return { syncStatus: 'degraded', syncSource: 'cached_fallback' };
          }
          return {
            features: DEFAULT_FALLBACK_FEATURES,
            quotas: {},
            isLoaded: true,
            syncStatus: 'unverified',
            syncSource: 'baseline_fallback'
          };
        });
      },

      resetFeatures: () => {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          // ignore storage errors
        }
        set({ ...initialState });
      },

      syncFeatures: async (targetCompanyId?: string | null, force: boolean = false) => {
        const state = get();

        // If company changed, invalidate old features
        if (targetCompanyId && targetCompanyId !== state.companyId) {
          set({ features: [], quotas: {}, isLoaded: false, companyId: targetCompanyId, syncStatus: 'unknown', syncSource: null });
        }

        // Return cached if loaded recently (within 5 minutes) and not forced
        const fiveMinutes = 5 * 60 * 1000;
        if (
          !force &&
          state.isLoaded &&
          state.lastSyncedAt &&
          Date.now() - state.lastSyncedAt < fiveMinutes &&
          (!targetCompanyId || state.companyId === targetCompanyId)
        ) {
          return state.features;
        }

        const syncKey = targetCompanyId || get().companyId || 'init';
        const existingSync = inFlightSync.get(syncKey);
        if (existingSync) return existingSync;

        set({ isSyncing: true });

        const syncPromise = (async () => {
          try {
            let activeFeatures: string[] = [];
            let activeQuotas: Record<string, any> = {};

            if (targetCompanyId) {
              const res = await api.get(`/companies/${targetCompanyId}`);
              const company = res?.data?.company;
              activeFeatures = company?.features || res?.data?.features || [];
              activeQuotas = company?.quotas || res?.data?.quotas || {};
              const syncStatus = company?.feature_sync || res?.data?.feature_sync;
              if (get().companyId && get().companyId !== targetCompanyId) return get().features;
              if (!Array.isArray(activeFeatures)) activeFeatures = DEFAULT_FALLBACK_FEATURES;
              set({
                features: activeFeatures,
                quotas: activeQuotas,
                isLoaded: true,
                lastSyncedAt: Date.now(),
                companyId: targetCompanyId || get().companyId,
                isSyncing: false,
                syncStatus: syncStatus?.status || 'unknown',
                syncSource: syncStatus?.source || null
              });
              return activeFeatures;
            } else {
              const res = await api.get('/companies/init');
              activeFeatures = res?.data?.features || [];
              activeQuotas = res?.data?.quotas || {};
              const syncStatus = res?.data?.feature_sync;
              if (!Array.isArray(activeFeatures)) activeFeatures = DEFAULT_FALLBACK_FEATURES;
              set({
                features: activeFeatures,
                quotas: activeQuotas,
                isLoaded: true,
                lastSyncedAt: Date.now(),
                companyId: get().companyId,
                isSyncing: false,
                syncStatus: syncStatus?.status || 'unknown',
                syncSource: syncStatus?.source || null
              });
              return activeFeatures;
            }
          } catch (error) {
            console.warn('[FeatureStore] Sync failed, maintaining fallback state:', error);
            const current = get();
            if (targetCompanyId && current.companyId && current.companyId !== targetCompanyId) {
              return current.features;
            }
            const sameCompany = !targetCompanyId || !current.companyId || current.companyId === targetCompanyId;
            const hasPriorPolicy = sameCompany && current.isLoaded;
            const fallback = hasPriorPolicy ? current.features : DEFAULT_FALLBACK_FEATURES;
            set({
              features: fallback,
              quotas: hasPriorPolicy ? current.quotas : {},
              isLoaded: true,
              isSyncing: false,
              companyId: targetCompanyId || current.companyId,
              syncStatus: hasPriorPolicy ? 'degraded' : 'unverified',
              syncSource: hasPriorPolicy ? 'cached_fallback' : 'baseline_fallback'
            });
            return fallback;
          } finally {
            if (inFlightSync.get(syncKey) === syncPromise) inFlightSync.delete(syncKey);
          }
        })();
        inFlightSync.set(syncKey, syncPromise);

        return syncPromise;
      }
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState, currentState) => {
        const persisted =
          persistedState && typeof persistedState === 'object'
            ? (persistedState as Partial<FeatureState>)
            : {};

        return {
          ...currentState,
          ...persisted,
          features: Array.isArray(persisted.features) ? persisted.features : currentState.features,
          quotas:
            persisted.quotas && typeof persisted.quotas === 'object' && !Array.isArray(persisted.quotas)
              ? persisted.quotas
              : currentState.quotas,
          isLoaded: typeof persisted.isLoaded === 'boolean' ? persisted.isLoaded : currentState.isLoaded,
          syncStatus: persisted.syncStatus || currentState.syncStatus,
          syncSource: persisted.syncSource ?? currentState.syncSource
        };
      },
      partialize: (state) => ({
        features: state.features,
        quotas: state.quotas,
        isLoaded: state.isLoaded,
        lastSyncedAt: state.lastSyncedAt,
        companyId: state.companyId,
        syncStatus: state.syncStatus,
        syncSource: state.syncSource
      })
    }
  )
);

// Convenient React hooks
export const useFeature = (token: string): boolean => {
  return useFeatureStore((state) => (token ? (state?.features ?? []).includes(token) : true));
};

export const useFeatures = (): string[] => {
  return useFeatureStore((state) => state?.features ?? []);
};

export const useQuota = (key: string): any => {
  return useFeatureStore((state) => state?.quotas?.[key] ?? null);
};

export const useHasAllFeatures = (tokens: string[]): boolean => {
  return useFeatureStore((state) => {
    if (!tokens || tokens.length === 0) return true;
    return tokens.every((token) => (state?.features ?? []).includes(token));
  });
};

export const useHasAnyFeature = (tokens: string[]): boolean => {
  return useFeatureStore((state) => {
    if (!tokens || tokens.length === 0) return true;
    return tokens.some((token) => (state?.features ?? []).includes(token));
  });
};

export const useIsFeaturesLoaded = (): boolean => {
  return useFeatureStore((state) => state?.isLoaded ?? false);
};

// Non-reactive helper for callbacks, event handlers, and vanilla JS
export const checkFeature = (token: string): boolean => {
  return useFeatureStore.getState().hasFeature(token);
};

// Non-reactive direct sync helper
export const syncFeatures = (companyId?: string | null, force: boolean = false) => {
  return useFeatureStore.getState().syncFeatures(companyId, force);
};
